import React from "react";
import { Badge } from "@mantine/core";

import { IAkbarFlightDetails } from "libs/data-access/types";

interface IProps {
    journey: IAkbarFlightDetails;
    passenger: {
        paxId: number;
        title: string;
        firstName: string;
        lastName: string;
        gender: string;
        dateOfBirth: string;
        passengerCode: string;
        passportNumber: string | null;
        passportIssueDate: string | null;
        passportExpiryDate: string | null;
        infantDetail: {
            paxId: number;
            title: string;
            firstName: string;
            lastName: string;
            gender: string;
            dateOfBirth: string;
        } | null;
    };
};

const AkbarSsrDetails: React.FC<IProps> = ({ journey, passenger }) => {
    // Check if there are any SSRs for this passenger across all segments
    const hasAnySsr = journey.Segments?.some((segment) =>
        segment.SSR?.some((ssr) => ssr.paxId === passenger.paxId)
    );

    if (!hasAnySsr) {
        return (
            <div className="text-sm text-gray-500 italic">
                No services purchased
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-3">
            {journey.Segments?.map((segment, segmentIndex) => {
                const passengerSsrs = segment.SSR?.filter(
                    (ssr) => ssr.paxId === passenger.paxId
                );

                if (!passengerSsrs || passengerSsrs.length === 0) {
                    return null;
                }

                return (
                    <div
                        key={`segment-${segmentIndex}`}
                        className="border-l-2 border-blue-300 pl-3 py-1"
                    >
                        {/* Segment Header */}
                        <div className="flex items-center gap-2 mb-2">
                            <Badge size="sm" variant="light" color="blue" className="font-mono">
                                {segment.origin} → {segment.destination}
                            </Badge>
                            <Badge size="xs" variant="outline" color="gray">
                                {segment.VAC}
                            </Badge>
                        </div>

                        {/* SSR List */}
                        <div className="flex flex-col gap-2">
                            {passengerSsrs.map((eachSSR, ssrIndex) => {
                                const totalPrice = eachSSR.Charge + eachSSR.VAT;

                                return (
                                    <div
                                        key={`ssr-${ssrIndex}`}
                                        className="bg-gray-50 rounded-md p-2 text-sm"
                                    >
                                        {/* SSR Code and Description */}
                                        <div className="flex items-start gap-2 mb-1">
                                            <Badge size="xs" color="teal" variant="filled">
                                                {eachSSR.Code}
                                            </Badge>
                                            <span className="font-medium text-gray-700 flex-1">
                                                {eachSSR.Description}
                                            </span>
                                        </div>

                                        {/* Price Breakdown */}
                                        <div className="text-xs text-gray-600 ml-1">
                                            <span className="font-mono">
                                                ₹{eachSSR.Charge.toFixed(2)} + ₹{eachSSR.VAT.toFixed(2)} =
                                                <span className="font-semibold text-gray-800"> ₹{totalPrice.toFixed(2)}</span>
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AkbarSsrDetails;
